// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * The building's rooms, derived from the survey: each part carries the room
 * it sits in (`room_id`, `room_name`, from the rooms cloud at sync time), so
 * a room's types, parts and quantity are a fold over the parts. Rooms the
 * summary reports without any part (`rooms_without_parts`) are merged in as
 * empty rooms. Centroids, boxes and storeys come from `GET /rooms` /
 * `GET /storeys` (or the Ground's streamed pages, `bygning/spatial.ts`), not
 * from here; this module only groups the rooms by storey for the room
 * picker (`roomScopeGroups`).
 * Pure: no React, no DOM.
 */

import type { SurveySummary, SurveyType } from '../api/types';
import type { Storey } from './storeys';

export interface RoomInfo {
  /** The room id from the rooms cloud; null for a room known only by name. */
  id: number | null;
  /** Stable React key: `r<id>` or `n<name>`. */
  key: string;
  name: string;
  /** Distinct non-rejected types with a part in the room. */
  types: number;
  /** Parts of non-rejected types in the room. */
  parts: number;
  /** Summed part quantity — mixed units, so a size hint only. */
  quantity: number;
  /** Summed quantity per unit (`{ m²: 412, stk: 3 }`). */
  quantityByUnit: Record<string, number>;
  /** Reported by the summary as having no registered parts. */
  empty: boolean;
}

/** The label of a part's room when the scan put it in none. */
export const NO_ROOM_NAME = 'Uden rum';

function roomLabel(id: number | null, name: string): string {
  const trimmed = name.trim();
  if (trimmed) return trimmed;
  return id === null ? NO_ROOM_NAME : `Rum ${id}`;
}

/**
 * Rooms with their counts, ordered by id (rooms known only by name after,
 * alphabetically), then the summary's empty rooms not already listed.
 * Rejected types are fejldetektion and count for nothing.
 */
export function roomsOf(types: readonly SurveyType[], summary?: SurveySummary | null): RoomInfo[] {
  const byKey = new Map<string, RoomInfo & { typeIds: Set<number> }>();
  for (const t of types) {
    if (t.review_status === 'rejected') continue;
    for (const p of t.parts) {
      const name = roomLabel(p.room_id, p.room_name);
      const key = p.room_id !== null ? `r${p.room_id}` : `n${name}`;
      let room = byKey.get(key);
      if (!room) {
        room = { id: p.room_id, key, name, types: 0, parts: 0, quantity: 0, quantityByUnit: {}, empty: false, typeIds: new Set() };
        byKey.set(key, room);
      }
      room.typeIds.add(t.id);
      room.parts += 1;
      room.quantity += p.quantity;
      room.quantityByUnit[t.unit] = (room.quantityByUnit[t.unit] ?? 0) + p.quantity;
    }
  }
  const rooms: RoomInfo[] = [...byKey.values()].map(({ typeIds, ...room }) => ({ ...room, types: typeIds.size }));
  rooms.sort((a, b) => {
    if (a.id !== null && b.id !== null) return a.id - b.id;
    if (a.id !== null) return -1;
    if (b.id !== null) return 1;
    return a.name.localeCompare(b.name, 'da');
  });
  const known = new Set(rooms.map((r) => r.name.toLowerCase()));
  for (const raw of summary?.rooms_without_parts ?? []) {
    const name = raw.trim();
    if (!name || known.has(name.toLowerCase())) continue;
    known.add(name.toLowerCase());
    rooms.push({ id: null, key: `n${name}`, name, types: 0, parts: 0, quantity: 0, quantityByUnit: {}, empty: true });
  }
  return rooms;
}

/** The ids of the rooms that can be focused (`?room=` needs an id), in order. */
export function roomIds(rooms: readonly RoomInfo[]): number[] {
  return rooms.flatMap((r) => (r.id === null ? [] : [r.id]));
}

/** The name of room `id`, or null when it is not in the list. */
export function roomName(rooms: readonly RoomInfo[], id: number | null): string | null {
  if (id === null) return null;
  return rooms.find((r) => r.id === id)?.name ?? null;
}

/** One `<optgroup>` of the room picker; `label: null` = no group (a building without storeys). */
export interface RoomScopeGroup {
  label: string | null;
  rooms: RoomInfo[];
}

/** The picker's group for rooms on no known storey. */
export const OTHER_ROOMS_LABEL = 'Andre rum';

/**
 * The room picker's options (`RoomScope`): one group per storey (bottom up,
 * storeys with none of the rooms skipped), its rooms in `rooms`' order, then
 * "Andre rum" for the rest. Without storeys, one flat group. Only rooms that
 * can be focused (an id) are offered; no such room is no groups.
 */
export function roomScopeGroups(rooms: readonly RoomInfo[], storeys: readonly Storey[]): RoomScopeGroup[] {
  const focusable = rooms.filter((r) => r.id !== null);
  if (focusable.length === 0) return [];
  if (storeys.length === 0) return [{ label: null, rooms: focusable }];
  const placed = new Set<number>();
  const groups: RoomScopeGroup[] = [];
  for (const s of storeys) {
    const on = focusable.filter((r) => r.id !== null && !placed.has(r.id) && s.roomIds.includes(r.id));
    if (on.length === 0) continue;
    for (const r of on) placed.add(r.id as number);
    groups.push({ label: s.name, rooms: on });
  }
  const rest = focusable.filter((r) => r.id !== null && !placed.has(r.id));
  if (rest.length > 0) groups.push({ label: OTHER_ROOMS_LABEL, rooms: rest });
  return groups;
}
